import { useCallback, useEffect, useRef, useState } from 'react';
import { Campaign, Task, TaskComment, TaskStatus, User } from '../../types';
import { campaignRepository } from '../../platform/firebase/campaignRepository';
import { subscribeToUsers } from '../../services/users';
import {
  adminAssignReview,
  adminSetTaskStatus,
  archiveTask,
  fetchTasksForBrowse,
  subscribeToTask,
} from '../../services/tasks';
import { subscribeToComments } from '../../services/taskComments';
import { rerunTaskReviewer } from '../../services/taskReviewer';
import { isTaskCommandError } from '../../services/taskCommands';
import { logGlobalError } from '../../utils/globalErrorLogger';
import {
  BrowseTasksFilter,
  EMPTY_BROWSE_FILTER,
  validateBrowseFilter,
} from './filter';

export interface BrowseTasksToolViewModel {
  busy: boolean;
  error: string | null;

  // ---- Form data sources ----
  campaigns: Campaign[];
  users: User[];

  // ---- Filter state ----
  filter: BrowseTasksFilter;
  setFilter: (patch: Partial<BrowseTasksFilter>) => void;
  resetFilter: () => void;

  // ---- Search ----
  searching: boolean;
  searchError: string | null;
  hasSearched: boolean;
  /** Human-readable "why is the Search button disabled" message, or null. */
  searchBlockedReason: string | null;
  run: () => Promise<void>;

  // ---- Results ----
  results: Task[];
  selectedIndex: number;
  selectedTask: Task | null;
  comments: TaskComment[];
  next: () => void;
  prev: () => void;

  // ---- Admin tombstone ----
  tombstoning: boolean;
  tombstoneError: string | null;
  tombstoneSelected: () => Promise<void>;

  // ---- Admin status repair ----
  updatingStatus: boolean;
  statusChangeError: string | null;
  changeSelectedStatus: (status: TaskStatus) => Promise<void>;

  // ---- Admin review assignment ----
  assigningReviewer: boolean;
  reviewerAssignmentError: string | null;
  assignSelectedReview: (reviewerId: string) => Promise<void>;

  // ---- Admin AI-reviewer rerun ----
  rerunningReviewer: boolean;
  /** Latest rerun outcome the UI should render. Cleared when the selection
   *  changes or when a new rerun starts. */
  reviewerRerunStatus:
    | { kind: 'success'; commentsAdded: number; commentsCleared: number }
    | { kind: 'report' }
    | { kind: 'error'; message: string }
    | null;
  rerunReviewerForSelected: () => Promise<void>;

  initialize: () => void;
  dispose: () => void;
}

export function useBrowseTasksToolViewModel(): BrowseTasksToolViewModel {
  const [busy, setBusy] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const [filter, setFilterState] = useState<BrowseTasksFilter>(EMPTY_BROWSE_FILTER);
  const [searching, setSearching] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const [results, setResults] = useState<Task[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [tombstoning, setTombstoning] = useState<boolean>(false);
  const [tombstoneError, setTombstoneError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<boolean>(false);
  const [statusChangeError, setStatusChangeError] = useState<string | null>(null);
  const [assigningReviewer, setAssigningReviewer] = useState<boolean>(false);
  const [reviewerAssignmentError, setReviewerAssignmentError] = useState<string | null>(null);
  const [rerunningReviewer, setRerunningReviewer] = useState<boolean>(false);
  const [reviewerRerunStatus, setReviewerRerunStatus] = useState<
    BrowseTasksToolViewModel['reviewerRerunStatus']
  >(null);

  const initUnsubsRef = useRef<Array<() => void>>([]);
  const taskUnsubRef = useRef<(() => void) | null>(null);
  const commentsUnsubRef = useRef<(() => void) | null>(null);

  // ---- Filter setters (patch-style so the form can toggle one field at a time) ----

  const setFilter = useCallback((patch: Partial<BrowseTasksFilter>) => {
    setFilterState((prev) => ({ ...prev, ...patch }));
  }, []);

  const resetFilter = useCallback(() => {
    setFilterState(EMPTY_BROWSE_FILTER);
    setResults([]);
    setSelectedIndex(0);
    setSelectedTask(null);
    setComments([]);
    setHasSearched(false);
    setSearchError(null);
  }, []);

  // ---- Subscriptions lifecycle ----

  const disposeSelection = useCallback(() => {
    taskUnsubRef.current?.();
    taskUnsubRef.current = null;
    commentsUnsubRef.current?.();
    commentsUnsubRef.current = null;
  }, []);

  const dispose = useCallback(() => {
    initUnsubsRef.current.forEach((u) => u());
    initUnsubsRef.current = [];
    disposeSelection();
  }, [disposeSelection]);

  const initialize = useCallback(() => {
    setBusy(true);
    setError(null);
    dispose();

    let campaignsLoaded = false;
    let usersLoaded = false;
    const maybeDone = () => {
      if (campaignsLoaded && usersLoaded) setBusy(false);
    };

    const unsubCampaigns = campaignRepository.subscribeAll(
      (next) => {
        setCampaigns(next);
        campaignsLoaded = true;
        maybeDone();
      },
      (err) => {
        setError(err.message);
        setBusy(false);
      },
    );
    const unsubUsers = subscribeToUsers(
      (next) => {
        setUsers(next);
        usersLoaded = true;
        maybeDone();
      },
      (err) => {
        setError(err.message);
        setBusy(false);
      },
    );
    initUnsubsRef.current = [unsubCampaigns, unsubUsers];
  }, [dispose]);

  useEffect(() => () => dispose(), [dispose]);

  // ---- Search ----

  const searchBlockedReason = validateBrowseFilter(filter);

  // Keep a live snapshot of `users` in a ref so `run()` can resolve the
  // typed owner email without being re-memoised every time the subscription
  // fires. The form's email input is resolved at click time, not on every
  // keystroke, so a stable callback is plenty.
  const usersRef = useRef<User[]>([]);
  usersRef.current = users;

  const run = useCallback(async () => {
    const reason = validateBrowseFilter(filter);
    if (reason) {
      setSearchError(reason);
      return;
    }
    // Resolve the typed email to a uid against the live users list.
    // Emails are case-insensitive; whitespace is ignored.
    let ownerId = filter.ownerUid.trim() || undefined;
    const typedEmail = filter.ownerEmail.trim();
    if (!filter.taskId.trim() && !ownerId && typedEmail) {
      const needle = typedEmail.toLowerCase();
      const match = usersRef.current.find(
        (u) => (u.email ?? '').toLowerCase() === needle,
      );
      if (!match) {
        setSearchError(`No user with email "${typedEmail}".`);
        return;
      }
      ownerId = match.uid;
    }

    setSearching(true);
    setSearchError(null);
    try {
      const tasks = await fetchTasksForBrowse({
        taskId: filter.taskId.trim() || undefined,
        campaignId: filter.campaignId || undefined,
        taskTypeId: filter.taskTypeId || undefined,
        status: filter.status || undefined,
        ownerId,
      });
      setResults(tasks);
      setSelectedIndex(0);
      setHasSearched(true);
    } catch (e: any) {
      setSearchError(e?.message ?? 'Failed to query tasks.');
      setResults([]);
      logGlobalError(e, {
        context: 'BrowseTasks.run',
        metadata: {
          taskId: filter.taskId || null,
          campaignId: filter.campaignId || null,
          taskTypeId: filter.taskTypeId || null,
          status: filter.status || null,
        },
      });
    } finally {
      setSearching(false);
    }
  }, [filter]);

  // ---- Live subscription for the currently-selected task + its comments ----
  //
  // When the result list or selected index changes, point the subscription at
  // the new task id. Keeping subscriptions keyed on `selectedTaskId` means we
  // don't tear down / rebuild on unrelated state churn, and we automatically
  // reflect real-time edits (e.g. the LLM reviewer writing comments while
  // the admin is looking at the task).
  const selectedTaskId = results[selectedIndex]?.id ?? null;

  useEffect(() => {
    disposeSelection();
    setReviewerRerunStatus(null);
    setStatusChangeError(null);
    setReviewerAssignmentError(null);
    if (!selectedTaskId) {
      setSelectedTask(null);
      setComments([]);
      return;
    }
    taskUnsubRef.current = subscribeToTask(
      selectedTaskId,
      (task) => setSelectedTask(task),
      (err) => setSearchError(err.message),
    );
    commentsUnsubRef.current = subscribeToComments(
      selectedTaskId,
      (next) => setComments(next),
      (err) => setSearchError(err.message),
    );
  }, [selectedTaskId, disposeSelection]);

  // ---- Prev / Next ----

  const next = useCallback(() => {
    setSelectedIndex((i) => Math.min(i + 1, Math.max(0, results.length - 1)));
  }, [results.length]);

  const prev = useCallback(() => {
    setSelectedIndex((i) => Math.max(0, i - 1));
  }, []);

  // ---- Admin tombstone ----
  //
  // Soft-deletes the currently-selected task by flipping its status to
  // `archived` and drops it from the local results list so the admin can
  // keep stepping through what's left without an extra round-trip. The
  // task's own subscription will tear down naturally when `selectedTaskId`
  // shifts to the next row.
  const tombstoneSelected = useCallback(async () => {
    const target = results[selectedIndex];
    if (!target) return;
    setTombstoning(true);
    setTombstoneError(null);
    try {
      await archiveTask(target.id);
      setResults((prev) => prev.filter((t) => t.id !== target.id));
      setSelectedIndex((i) => Math.max(0, Math.min(i, results.length - 2)));
    } catch (e: any) {
      setTombstoneError(e?.message ?? 'Failed to delete task.');
      logGlobalError(e, {
        context: 'BrowseTasks.tombstoneSelected',
        metadata: { taskId: target.id },
      });
    } finally {
      setTombstoning(false);
    }
  }, [results, selectedIndex]);

  // ---- Admin status repair ----
  //
  // Lets an admin recover tasks from Browse Tasks without jumping into a
  // script. The service handles status-specific cleanup; the local patch keeps
  // the header responsive while the live task subscription catches up.
  const changeSelectedStatus = useCallback(async (status: TaskStatus) => {
    const resultTask = results[selectedIndex];
    const target = resultTask && selectedTask?.id === resultTask.id
      ? selectedTask
      : resultTask;
    if (!target || target.status === status) return;
    setUpdatingStatus(true);
    setStatusChangeError(null);
    const localPatch: Partial<Task> = {
      status,
      updatedAt: new Date(),
      ...(status === 'available'
        ? {
            ownerId: null,
            ownerName: null,
            expertId: null,
            claimedAt: null,
            reviewerId: null,
            reviewerName: null,
          }
        : {}),
      ...(status === 'in_review' || status === 'changes_requested'
        ? {
            reviewerId: null,
            reviewerName: null,
          }
        : {}),
      ...(status === 'approved' ? { reviewedAt: new Date() } : {}),
    };
    try {
      await adminSetTaskStatus(target.id, status);
      setResults((prev) => prev.map((task) => (
        task.id === target.id ? { ...task, ...localPatch } : task
      )));
      setSelectedTask((prev) => (prev && prev.id === target.id ? { ...prev, ...localPatch } : prev));
    } catch (e: any) {
      setStatusChangeError(e?.message ?? 'Failed to update task status.');
      logGlobalError(e, {
        context: 'BrowseTasks.changeSelectedStatus',
        metadata: { taskId: target.id, fromStatus: target.status, toStatus: status },
      });
    } finally {
      setUpdatingStatus(false);
    }
  }, [results, selectedIndex, selectedTask]);

  // ---- Admin review assignment ----

  const assignSelectedReview = useCallback(async (reviewerId: string) => {
    const resultTask = results[selectedIndex];
    const target = resultTask && selectedTask?.id === resultTask.id
      ? selectedTask
      : resultTask;
    if (!target || target.status !== 'in_review' || target.reviewerId === reviewerId) return;
    const reviewer = usersRef.current.find((user) => user.uid === reviewerId);
    const eligible = reviewer?.isAdmin === true
      || (reviewer?.isReviewer === true
        && (reviewer.reviewCampaignIds ?? []).includes(target.campaignId));
    if (!reviewer || !eligible) {
      setReviewerAssignmentError('Select an admin or a reviewer assigned to this campaign.');
      return;
    }

    setAssigningReviewer(true);
    setReviewerAssignmentError(null);
    try {
      await adminAssignReview(target.id, reviewerId, target.reviewRound, target.reviewerId);
      const reviewerName = reviewer.displayName?.trim()
        || reviewer.email?.trim()
        || reviewer.uid;
      const localPatch: Partial<Task> = {
        reviewerId,
        reviewerName,
        updatedAt: new Date(),
      };
      setResults((prev) => prev.map((task) => (
        task.id === target.id ? { ...task, ...localPatch } : task
      )));
      setSelectedTask((prev) => (
        prev && prev.id === target.id ? { ...prev, ...localPatch } : prev
      ));
    } catch (e: any) {
      setReviewerAssignmentError(e?.message ?? 'Failed to assign the review.');
      if (!isTaskCommandError(e)) {
        logGlobalError(e, {
          context: 'BrowseTasks.assignSelectedReview',
          metadata: {
            taskId: target.id,
            campaignId: target.campaignId,
            reviewerId,
          },
        });
      }
    } finally {
      setAssigningReviewer(false);
    }
  }, [results, selectedIndex, selectedTask]);

  // ---- Admin AI-reviewer rerun ----
  //
  // Calls the `rerunTaskReviewer` callable for the currently-selected task.
  // The trigger's automatic path (`onTaskEnterReview`) fires only on entry
  // transitions and respects the campaign's `llmReview.enabled` flag; this
  // path is the admin override that runs on demand against any non-terminal
  // task. Server-side clears pending+unaddressed LLM comments before writing
  // the fresh pass, so the human reviewer's accepted/rejected work is kept.
  const rerunReviewerForSelected = useCallback(async () => {
    const target = results[selectedIndex];
    if (!target) return;
    setRerunningReviewer(true);
    setReviewerRerunStatus(null);
    try {
      const result = await rerunTaskReviewer({ taskId: target.id });
      if (result.reportStatus === 'failed') {
        // The server already recorded and logged the failure on the report.
        setReviewerRerunStatus({
          kind: 'error',
          message: result.reportError || 'The AI review report could not be built.',
        });
      } else if (result.reportStatus === 'complete') {
        setReviewerRerunStatus({ kind: 'report' });
      } else {
        setReviewerRerunStatus({
          kind: 'success',
          commentsAdded: result.commentsAdded,
          commentsCleared: result.commentsCleared,
        });
      }
    } catch (e: any) {
      const message = e?.message ?? 'Failed to re-run the AI reviewer.';
      setReviewerRerunStatus({ kind: 'error', message });
      logGlobalError(e, {
        context: 'BrowseTasks.rerunReviewerForSelected',
        metadata: { taskId: target.id, taskType: target.taskType },
      });
    } finally {
      setRerunningReviewer(false);
    }
  }, [results, selectedIndex]);

  return {
    busy,
    error,
    campaigns,
    users,
    filter,
    setFilter,
    resetFilter,
    searching,
    searchError,
    hasSearched,
    searchBlockedReason,
    run,
    results,
    selectedIndex,
    selectedTask,
    comments,
    next,
    prev,
    tombstoning,
    tombstoneError,
    tombstoneSelected,
    updatingStatus,
    statusChangeError,
    changeSelectedStatus,
    assigningReviewer,
    reviewerAssignmentError,
    assignSelectedReview,
    rerunningReviewer,
    reviewerRerunStatus,
    rerunReviewerForSelected,
    initialize,
    dispose,
  };
}
